/**
 * Phones: a stock card — or a scanned shelf label / box barcode (18 #10 "Scan & move") — opens this bottom
 * sheet: the item's numbers, which store and shelf it is on, which sites need it, and Issue to site /
 * Transfer / Reserve / Adjust as big buttons. Built on the foundation BottomSheet + ActionBar.
 */
import { useEffect, useState } from 'react';
import { ArrowRightLeft, Lock, MapPin, Scale, Truck } from 'lucide-react';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { ActionBar } from '@/components/ui/action-bar';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { AdjustStockForm } from './StockItemDetail';
import ScannedStrip from './ScannedStrip';
import {
  INVENTORY_LOCATIONS,
  STOCK_STATUS_LABEL,
  STOCK_STATUS_TONE,
  availableOf,
  binOf,
  formatQty,
  locationColor,
  onHandOf,
  reservedOf,
  stockLocations,
  toQty
} from './inventoryModel';

export default function StockItemSheet({ item, status, demand = [], scanned = null, onOpenChange, onIssue, onTransfer, onReserve, onAdjust, canEdit = true }) {
  const [adjusting, setAdjusting] = useState(false);
  useEffect(() => { setAdjusting(false); }, [item?.part_number]);
  const open = Boolean(item);
  const available = item ? availableOf(item) : 0;
  const where = item ? stockLocations(item) : [];
  const locations = INVENTORY_LOCATIONS.map((location) => ({ location, qty: toQty(item?.location_quantities?.[location]) }))
    .sort((left, right) => right.qty - left.qty);
  const otherQty = where.slice(1).reduce((sum, entry) => sum + entry.qty, 0);
  const shelf = where[0] ? binOf(item, where[0].location) : '';
  const close = () => onOpenChange(false);

  const actions = [
    { key: 'issue', label: 'Issue to site', icon: Truck, variant: 'primary', disabled: !item || onHandOf(item) <= 0, hint: 'Nothing in stock', onSelect: () => { close(); onIssue?.(); }, testId: 'stock-sheet-issue' },
    { key: 'transfer', label: 'Transfer', icon: ArrowRightLeft, disabled: available <= 0, hint: 'Nothing available', onSelect: () => { close(); onTransfer?.(); }, testId: 'stock-sheet-transfer' },
    { key: 'reserve', label: 'Reserve', icon: Lock, disabled: available <= 0, hint: 'Nothing available', onSelect: () => { close(); onReserve?.(); }, testId: 'stock-sheet-reserve' },
    { key: 'adjust', label: 'Adjust', icon: Scale, onSelect: () => setAdjusting(true), testId: 'stock-sheet-adjust' }
  ];

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={<span className="font-mono text-[19px]">{item?.part_number || '—'}</span>}
      description={item?.description || 'No description'}
      headerAddon={status ? <StatusPill tone={STOCK_STATUS_TONE[status]} label={STOCK_STATUS_LABEL[status]} /> : null}
      banner={scanned && item ? <ScannedStrip scanned={scanned} /> : null}
      footer={item && canEdit && !adjusting ? <ActionBar actions={actions} ariaLabel={`Actions for ${item.part_number}`} testId="stock-sheet-actions" /> : null}
      testId="stock-item-sheet"
    >
      {item ? (
        <>
          <dl className="grid grid-cols-3 gap-2">
            {[
              ['On hand', onHandOf(item)],
              ['Reserved', reservedOf(item)],
              ['Available', available]
            ].map(([label, value], index) => (
              <div key={label} className={cn('rounded-xl border px-3 py-2.5', index === 2 ? 'border-[color:var(--status-green-bg)] bg-status-green-bg' : 'border-line bg-surface-2')}>
                <dt className={cn('text-[12px]', index === 2 ? 'text-status-green-ink' : 'text-ink-3')}>{label}</dt>
                <dd className="mt-0.5 font-heading text-[22px] font-black leading-none tabular-nums text-ink">{formatQty(value)}</dd>
              </div>
            ))}
          </dl>

          <p className="mt-3 flex flex-wrap items-center gap-x-2 text-[13px] text-ink-2" data-testid="stock-sheet-where">
            {where[0] ? (
              <>
                <span className="h-2 w-2 rounded-full" style={{ background: locationColor(where[0].location) }} aria-hidden="true" />
                <b className="font-semibold text-ink">{where[0].location} {formatQty(where[0].qty)}</b>
                {shelf ? <span className="rounded-md border border-line bg-surface-2 px-1.5 text-[12px] font-semibold text-ink-2">{shelf}</span> : null}
                <span className="text-ink-3">· other locations {formatQty(otherQty)}</span>
              </>
            ) : <span className="text-ink-3">Not in any store</span>}
          </p>
          {demand.length ? (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[13px] text-ink-3">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Needed on sites:
              {demand.slice(0, 3).map((entry, index) => (
                <span key={entry.project_id} className="font-semibold text-ink">{index ? '· ' : ''}{entry.project_name} {formatQty(entry.remaining_qty)}</span>
              ))}
              {demand.length > 3 ? <span>+{demand.length - 3}</span> : null}
            </p>
          ) : null}

          {!canEdit ? (
            <p className="mt-4 rounded-xl bg-surface-2 px-3 py-2.5 text-[13px] text-ink-3" data-testid="stock-sheet-view-only">View only — your role cannot change stock.</p>
          ) : adjusting ? (
            <AdjustStockForm
              item={item}
              locations={locations}
              onCancel={() => setAdjusting(false)}
              onSave={async (payload) => { await onAdjust?.(payload); setAdjusting(false); onOpenChange(false); }}
            />
          ) : null}
        </>
      ) : null}
    </BottomSheet>
  );
}
